import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

function useTheme() {
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return { dark, toggle: () => setDark((d) => !d) };
}

export function App() {
  const { dark, toggle: toggleTheme } = useTheme();
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<Operator>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === "0" ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    if (value !== 0) {
      setDisplay(String(-value));
    }
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const calculate = (left: number, right: number, op: Operator): number => {
    switch (op) {
      case "+": return left + right;
      case "-": return left - right;
      case "×": return left * right;
      case "÷": return right !== 0 ? left / right : 0;
      default: return right;
    }
  };

  const handleOperator = (nextOperator: Operator) => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operator && !waitingForOperand) {
      const result = calculate(previousValue, inputValue, operator);
      const resultStr = parseFloat(result.toFixed(10)).toString();
      setDisplay(resultStr);
      setPreviousValue(result);
    } else {
      setPreviousValue(inputValue);
    }

    setOperator(nextOperator);
    setWaitingForOperand(true);
  };

  const handleEquals = () => {
    if (previousValue === null || operator === null) return;

    const inputValue = parseFloat(display);
    const result = calculate(previousValue, inputValue, operator);
    const resultStr = parseFloat(result.toFixed(10)).toString();
    setDisplay(resultStr);
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const formatDisplay = (value: string): string => {
    if (value.includes(".") && !value.endsWith(".")) {
      const [intPart, decPart] = value.split(".");
      const num = parseInt(intPart, 10);
      return num.toLocaleString("en-US") + "." + decPart;
    }
    if (value.endsWith(".")) {
      const num = parseInt(value, 10);
      return num.toLocaleString("en-US") + ".";
    }
    const num = parseFloat(value);
    if (Number.isInteger(num) && !value.includes(".")) {
      return num.toLocaleString("en-US");
    }
    return value;
  };

  const displayFontSize = display.length > 12 ? "text-2xl" : display.length > 8 ? "text-3xl" : "text-5xl";

  const operatorButtons: { label: string; op: Operator }[] = [
    { label: "÷", op: "÷" },
    { label: "×", op: "×" },
    { label: "−", op: "-" },
    { label: "+", op: "+" },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/50 to-background p-4 transition-colors duration-300">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-full bg-secondary text-secondary-foreground hover:bg-accent transition-colors"
        aria-label="Toggle theme"
      >
        {dark ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
        )}
      </button>

      <Card className="w-full max-w-xs bg-card/80 backdrop-blur-xl border-border shadow-2xl rounded-3xl overflow-hidden transition-colors duration-300">
        <CardContent className="p-0">
          {/* Display */}
          <div className="px-6 pt-8 pb-4 min-h-[120px] flex flex-col justify-end items-end">
            {operator && previousValue !== null && (
              <div className="text-muted-foreground text-sm mb-1 font-mono">
                {previousValue.toLocaleString("en-US")} {operator}
              </div>
            )}
            <div className={`${displayFontSize} font-light text-foreground tracking-tight font-mono truncate max-w-full`}>
              {formatDisplay(display)}
            </div>
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-4 gap-[1px] bg-border/50 p-[1px]">
            {/* Row 1: AC, +/-, %, ÷ */}
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-muted hover:bg-muted/70 text-secondary-foreground text-lg font-medium border-0"
              onClick={clear}
            >
              {display === "0" && previousValue === null ? "AC" : "C"}
            </Button>
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-muted hover:bg-muted/70 text-secondary-foreground text-lg font-medium border-0"
              onClick={toggleSign}
            >
              +/−
            </Button>
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-muted hover:bg-muted/70 text-secondary-foreground text-lg font-medium border-0"
              onClick={inputPercent}
            >
              %
            </Button>
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "÷" && waitingForOperand
                  ? "bg-orange-100 !text-orange-500 hover:bg-orange-50 dark:bg-white dark:hover:bg-zinc-100"
                  : "bg-orange-500 hover:bg-orange-400"
              }`}
              onClick={() => handleOperator("÷")}
            >
              ÷
            </Button>

            {/* Row 2: 7, 8, 9, × */}
            {["7", "8", "9"].map((d) => (
              <Button
                key={d}
                variant="secondary"
                className="h-16 rounded-none bg-secondary hover:bg-secondary/70 text-secondary-foreground text-xl font-medium border-0"
                onClick={() => inputDigit(d)}
              >
                {d}
              </Button>
            ))}
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "×" && waitingForOperand
                  ? "bg-orange-100 !text-orange-500 hover:bg-orange-50 dark:bg-white dark:hover:bg-zinc-100"
                  : "bg-orange-500 hover:bg-orange-400"
              }`}
              onClick={() => handleOperator("×")}
            >
              ×
            </Button>

            {/* Row 3: 4, 5, 6, - */}
            {["4", "5", "6"].map((d) => (
              <Button
                key={d}
                variant="secondary"
                className="h-16 rounded-none bg-secondary hover:bg-secondary/70 text-secondary-foreground text-xl font-medium border-0"
                onClick={() => inputDigit(d)}
              >
                {d}
              </Button>
            ))}
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "-" && waitingForOperand
                  ? "bg-orange-100 !text-orange-500 hover:bg-orange-50 dark:bg-white dark:hover:bg-zinc-100"
                  : "bg-orange-500 hover:bg-orange-400"
              }`}
              onClick={() => handleOperator("-")}
            >
              −
            </Button>

            {/* Row 4: 1, 2, 3, + */}
            {["1", "2", "3"].map((d) => (
              <Button
                key={d}
                variant="secondary"
                className="h-16 rounded-none bg-secondary hover:bg-secondary/70 text-secondary-foreground text-xl font-medium border-0"
                onClick={() => inputDigit(d)}
              >
                {d}
              </Button>
            ))}
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "+" && waitingForOperand
                  ? "bg-orange-100 !text-orange-500 hover:bg-orange-50 dark:bg-white dark:hover:bg-zinc-100"
                  : "bg-orange-500 hover:bg-orange-400"
              }`}
              onClick={() => handleOperator("+")}
            >
              +
            </Button>

            {/* Row 5: 0 (span 2), ., = */}
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-secondary hover:bg-secondary/70 text-secondary-foreground text-xl font-medium border-0 col-span-2"
              onClick={() => inputDigit("0")}
            >
              0
            </Button>
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-secondary hover:bg-secondary/70 text-secondary-foreground text-xl font-medium border-0"
              onClick={inputDecimal}
            >
              .
            </Button>
            <Button
              className="h-16 rounded-none bg-orange-500 hover:bg-orange-400 text-white text-xl font-medium border-0"
              onClick={handleEquals}
            >
              =
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default App;
